import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button, Input, Card, Modal } from "@/components/ui";
import { authApi } from "@/services/api";
import { useAuthStore } from "@/stores/authStore";
import { useEmailValidation } from "@/hooks";

export default function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {},
  );
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState("");
  const [unlockCode, setUnlockCode] = useState("");

  // Email validation hook
  const emailValidation = useEmailValidation(email, 800);

  const loginMutation = useMutation({
    mutationFn: () => authApi.login(email, password),
    onSuccess: async (response) => {
      if (response.success && response.data) {
        const { user, token } = response.data;

        // Buscar dados da empresa se o usuário tiver uma
        let company = null;
        if (user.company_id) {
          try {
            const companyResponse = await authApi.getCompanyWithToken(
              user.company_id,
              token,
            );
            if (companyResponse.success) {
              company = companyResponse.data;
            }
          } catch (error) {
            console.error("Erro ao carregar dados da empresa:", error);
          }
        }

        login(user, token, company);
        toast.success("Login realizado com sucesso!");
        navigate(user.role === "client" ? "/client" : "/admin");
      }
    },
    onError: (error: any) => {
      if (error?.status === 423) {
        setUnlockEmail(email);
        setShowUnlockModal(true);
      }
      toast.error(error.message || "Erro ao fazer login");
    },
  });

  const requestUnlockMutation = useMutation({
    mutationFn: () => authApi.requestUnlock(unlockEmail),
    onSuccess: () => {
      toast.success("Código enviado para seu email");
    },
    onError: (error: any) => {
      toast.error(error.message || "Erro ao solicitar desbloqueio");
    },
  });

  const unlockMutation = useMutation({
    mutationFn: () => authApi.unlock(unlockEmail, unlockCode),
    onSuccess: () => {
      toast.success("Conta desbloqueada. Faça login novamente.");
      setShowUnlockModal(false);
      setUnlockCode("");
    },
    onError: (error: any) => {
      toast.error(error.message || "Código inválido");
    },
  });

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!email) {
      newErrors.email = "Email é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email inválido";
    } else if (emailValidation.valid === false) {
      newErrors.email = "Email inválido ou não existe";
    }

    if (!password) {
      newErrors.password = "Senha é obrigatória";
    } else if (password.length < 6) {
      newErrors.password = "Senha deve ter no mínimo 6 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      loginMutation.mutate();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-primary/20 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-secondary/20 rounded-full blur-[128px]" />
      </div>

      <Card className="w-full max-w-md relative z-10" hover={false}>
        <div className="text-center mb-8">
          <h1 className="font-script text-4xl text-accent-primary mb-2">
            VintageLab
          </h1>
          <p className="text-text-secondary">
            Entre na sua conta para continuar
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="email"
            label="Email"
            placeholder="maria.eduarda@vintagelab.com.br"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            leftIcon={<Mail className="w-5 h-5" />}
            error={errors.email}
            hint={
              emailValidation.isValidating
                ? "Validando..."
                : emailValidation.message
            }
            className={
              emailValidation.valid === true
                ? "border-green-500"
                : emailValidation.valid === false
                  ? "border-red-500"
                  : ""
            }
          />

          <Input
            type={showPassword ? "text" : "password"}
            label="Senha"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            leftIcon={<Lock className="w-5 h-5" />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="hover:text-text-primary transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            }
            error={errors.password}
          />

          <div className="flex items-center justify-end text-sm">
            <span className="text-text-muted">
              Esqueceu a senha? Contate o administrador.
            </span>
          </div>

          <Button
            type="submit"
            className="w-full"
            isLoading={loginMutation.isPending}
          >
            Entrar
          </Button>
        </form>
      </Card>

      <Modal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        title="Conta bloqueada"
        description="Solicite o código por email e informe abaixo para desbloquear."
        size="sm"
      >
        <div className="space-y-4">
          <Input
            type="email"
            label="Email"
            value={unlockEmail}
            onChange={(e) => setUnlockEmail(e.target.value)}
            placeholder="maria.eduarda@vintagelab.com.br"
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              isLoading={requestUnlockMutation.isPending}
              onClick={() => requestUnlockMutation.mutate()}
            >
              Enviar código
            </Button>
          </div>
          <Input
            type="text"
            label="Código"
            value={unlockCode}
            onChange={(e) => setUnlockCode(e.target.value)}
            placeholder="Informe o código"
          />
          <Button
            type="button"
            className="w-full"
            isLoading={unlockMutation.isPending}
            onClick={() => unlockMutation.mutate()}
          >
            Desbloquear
          </Button>
        </div>
      </Modal>
    </div>
  );
}
